import { Fragment, useEffect, useRef, useState } from 'react';
import { S } from '../../domain/state.js';
import { fmt, todayISO, iso10, fISO, addDays, wDY } from '../../lib/format.js';
import { cat } from '../../domain/categories.js';
import { getTxSrc, srcIcon, srcLabel, isExcluded, filterActive, pendingSum, sumBy } from '../../domain/tx.js';
import { keyAdd, periodLabel, periodShort, periodOptions, curKey, shiftDateToPeriod, keyOfISO } from '../../domain/periods.js';
import { applyTxFilter, txfCount } from '../../domain/txFilter.js';
import { Sheet, Empty, confirmDialog } from '../../components/ui.jsx';
import { Icon } from '../../components/Icon.jsx';
import { useMedia } from '../../lib/useMedia.js';
import { openModal, closeModal } from '../../app/nav.js';
import { UI, setUi, setTxf, useApp, toast } from '../../store/ui.js';
import { insertTx, deleteTx, deleteTxs, toggleExclude, moveTxPeriod, copyPeriod } from '../../store/actions.js';
import { openEditTx } from './EditTx.jsx';
import { TxFilters, TxfResult, clearTxf } from './TxFilters.jsx';

export function Transactions() {
  useApp();
  const [view, setView] = useState('list'); // list | mer
  const [menu, setMenu] = useState(false);
  const key = UI.viewKey, f = UI.txf;
  // הבסיס: התקופה שבכותרת, או כל התקופות
  const base = f.all ? S.tx : S.tx.filter((t) => t.month === key);
  const { txs, hidden } = applyTxFilter(base, f);
  const filtered = txfCount(f) > 0;
  return (
    <>
      <Summary txs={txs} k={key} filtered={filtered} />
      <div className="toolbar">
        <TxFilters base={base} />
        <div className="txf-more">
          <button className="icon-btn" onClick={() => setMenu(!menu)} aria-label="עוד" style={{ border: '1px solid var(--border2)', background: 'var(--surface)' }}><Icon name="more" /></button>
          {menu && <TxMenu view={view} setView={(v) => { setView(v); setMenu(false); }} close={() => setMenu(false)} k={key} />}
        </div>
      </div>
      <TxfResult txs={txs} hidden={hidden} />
      {view === 'mer' ? <Merchants txs={txs} filtered={filtered} />
        : <TxList txs={txs} filtered={filtered} hidden={hidden} mode={f.sort === 'amount' ? 'flat' : f.all ? 'period' : 'day'} />}
    </>
  );
}

function Summary({ txs, k, filtered }) {
  const act = filterActive(txs);
  const inc = sumBy(act, 'income'), exp = sumBy(act, 'expense'), bal = inc - exp, pend = pendingSum(txs);
  // השוואה לתקופה הקודמת רק כשמסתכלים על כל התקופה, בלי סינון
  const prevExp = filtered ? 0 : sumBy(filterActive(S.tx.filter((t) => t.month === keyAdd(k, -1))), 'expense');
  const diff = prevExp > 0 ? Math.round(((exp - prevExp) / prevExp) * 100) : 0;
  return (
    <div className={'summary-strip' + (filtered ? ' filtered' : '')}>
      <div className="sum-item"><div className="sum-lbl">הכנסות</div><div className="sum-val green">{fmt(inc)}</div></div>
      <div className="sum-item"><div className="sum-lbl">הוצאות</div><div className="sum-val red">{fmt(exp)}</div>
        {pend > 0 ? <div className="sum-cmp gold">⏳ {fmt(pend)} טרם חויב</div>
          : diff !== 0 && <div className="sum-cmp" style={{ color: diff > 0 ? 'var(--red)' : 'var(--green)' }}>{diff > 0 ? '▲' : '▼'}{Math.abs(diff)}% מהתקופה הקודמת</div>}
      </div>
      <div className="sum-item"><div className="sum-lbl">הכנסות פחות הוצאות</div><div className="sum-val" style={{ color: bal >= 0 ? 'var(--green)' : 'var(--red)' }}>{fmt(bal, bal < 0 ? '-' : '')}</div></div>
    </div>
  );
}

function TxMenu({ view, setView, close, k }) {
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target) && !e.target.closest('[aria-label="עוד"]')) close(); };
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, [close]);
  const item = (on, label, fn, danger) => <button className={'txmi' + (on ? ' on' : '')} style={danger ? { color: 'var(--red)' } : undefined} onClick={fn}>{label}</button>;
  return (
    <div ref={ref} className="menu-pop">
      {item(view === 'list', <><Icon name="list" size={17} />רשימת פעולות</>, () => setView('list'))}
      {item(view === 'mer', <><Icon name="wallet" size={17} />מקובץ לפי עסק</>, () => setView('mer'))}
      <div className="divider" />
      {item(false, <><Icon name="download" size={17} />ייצוא התקופה ל-Excel</>, () => { close(); exportCSV(k); })}
      {item(false, <><Icon name="copy" size={17} />העתקת התקופה</>, () => { close(); openModal(() => <CopyPeriod fromKey={k} />); })}
      {item(false, <><Icon name="trash" size={17} />מחיקת התקופה</>, () => { close(); deletePeriod(k); }, true)}
    </div>
  );
}

function dateLbl(d) {
  const t = todayISO(), y = fISO(addDays(new Date(), -1));
  return d === t ? 'היום' : d === y ? 'אתמול' : new Date(d + 'T12:00:00').toLocaleDateString('he-IL', { weekday: 'short', day: 'numeric', month: 'short' });
}

/** day: לפי ימים (רגיל) · period: לפי תקופות (כל התקופות) · flat: רשימה אחת (מיון לפי סכום) */
function TxList({ txs, filtered, hidden, mode }) {
  if (!txs.length) return (
    <Empty emo={filtered ? '🔍' : '📋'}>
      {filtered ? <>אין פעולות שמתאימות לסינון<br /><button className="ubtn ubtn-outline btn-sm mt10" onClick={clearTxf}>נקה סינון</button></>
        : hidden.n ? 'כל הפעולות בתקופה הן חיובים קבועים מוסתרים' : 'אין פעולות בתקופה הזו'}
    </Empty>
  );
  const groups = [], gm = {};
  const gkey = (t) => (mode === 'day' ? iso10(t.date) : mode === 'period' ? t.month || '' : '');
  txs.forEach((t) => { const g = gkey(t); if (!gm[g]) { gm[g] = []; groups.push(g); } gm[g].push(t); });
  return (
    <div className="txlist">
      <div className="tx-head"><span /><span>תיאור</span><span>קטגוריה</span><span>מקור</span><span style={{ textAlign: 'left' }}>סכום</span><span /></div>
      {groups.map((g) => {
        const list = gm[g], act = filterActive(list);
        const de = sumBy(act, 'expense'), di = sumBy(act, 'income');
        const head = mode === 'day' ? dateLbl(g) : mode === 'period' ? (g ? periodShort(g) : 'ללא תקופה') + ' · ' + (list.length === 1 ? 'פעולה אחת' : list.length + ' פעולות') : null;
        return (
          <Fragment key={g || 'all'}>
            {head && (
              <div className={'tx-day' + (mode === 'period' ? ' per' : '')} onClick={mode === 'period' && g ? () => { setUi({ viewKey: g }); setTxf({ all: false }); } : undefined}
                title={mode === 'period' ? 'מעבר לתקופה הזו' : undefined}>
                <span>{head}</span>
                <span className="tx-group-total">{di > 0 && <span className="green">{fmt(di, '+')}</span>}{de > 0 && <span className="red">{fmt(de, '−')}</span>}</span>
              </div>
            )}
            {list.map((t) => <TxRow t={t} key={t.id} showDate={mode !== 'day'} />)}
          </Fragment>
        );
      })}
    </div>
  );
}

function TxRow({ t, showDate }) {
  const [open, setOpen] = useState(false);
  const desktop = useMedia('(min-width: 1024px)');
  const c = cat(t.type, t.cat), ex = isExcluded(t), inc = t.type === 'income', src = getTxSrc(t);
  const meta = [showDate && wDY(t.date), !desktop && c.label, !desktop && srcLabel(src), t.event && '🏷️ ' + t.event, ex && (desktop ? 'מוחרגת מהסיכומים' : 'מוחרג')].filter(Boolean).join(' · ');
  async function del() {
    if (!(await confirmDialog({ title: 'מחיקת פעולה', body: '"' + t.description + '" ' + fmt(t.amount) + '. אי אפשר לבטל.', okLabel: 'מחק', danger: true }))) return;
    try { await deleteTx(t.id); toast('🗑️ נמחק'); } catch (e) { toast('שגיאה: ' + e.message); }
  }
  const acts = [
    ['edit', 'עריכה', () => openEditTx(t.id)],
    ['copy', 'שכפול', () => openModal(() => <DupTx t={t} />)],
    ['calendar', 'העברה לתקופה', () => openModal(() => <MovePeriod t={t} />)],
    [ex ? 'check' : 'eyeOff', ex ? 'החזרה לסיכומים' : 'החרגה מהסיכומים', () => toggleExclude(t)],
    ['trash', 'מחיקה', del, 'danger'],
  ];
  return (
    <div className={'txr' + (open ? ' open' : '') + (ex ? ' dim' : '')}>
      <div className="txr-main" onClick={() => (desktop ? openEditTx(t.id) : setOpen(!open))} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && e.target === e.currentTarget && openEditTx(t.id)}>
        <div className={'txic ' + t.type}>{c.icon}</div>
        <div className="txr-body"><div className="txr-name">{t.description}</div>{meta && <div className="txr-meta">{meta}</div>}</div>
        <div className="txr-cat">{c.label}</div>
        <div className="txr-src">{srcIcon(src)} {srcLabel(src)}</div>
        <div className="txr-amt" style={{ color: inc ? 'var(--green)' : 'var(--ink)', textDecoration: ex ? 'line-through' : undefined }}>{fmt(t.amount, inc ? '+' : '−')}</div>
        <div className="txr-quick" onClick={(e) => e.stopPropagation()}>
          {acts.map(([ico, label, fn, cls]) => <button key={ico} className={cls} title={label} aria-label={label} onClick={fn}><Icon name={ico} size={16} /></button>)}
        </div>
      </div>
      {open && !desktop && (
        <div className="txr-acts">
          {acts.map(([ico, label, fn, cls]) => <button key={ico} className={cls} onClick={fn}><Icon name={ico} size={15} />{label.split(' ')[0]}</button>)}
        </div>
      )}
    </div>
  );
}

function PeriodSelect({ value, onChange, extra }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      {periodOptions(extra).map((k) => <option key={k} value={k}>{periodLabel(k)}{k === curKey() ? ' (נוכחית)' : ''}</option>)}
    </select>
  );
}

function MovePeriod({ t }) {
  const cur = t.month || keyOfISO(t.date);
  const [k, setK] = useState(cur);
  return (
    <Sheet title="📅 העברה לתקופה אחרת">
      <div className="sub mb12">"{t.description}" · <b>{fmt(t.amount)}</b> · {iso10(t.date)}</div>
      <label className="lbl">שייך לתקופה</label>
      <PeriodSelect value={k} onChange={setK} extra={[cur]} />
      <div className="note mt6">הפעולה תיספר בתקופה שתבחר: בהוצאות, בהכנסות וביתרת הסגירה. התאריך שלה לא משתנה.</div>
      <button className="ubtn ubtn-primary w100 mt12" onClick={async () => {
        if (k !== cur) { try { await moveTxPeriod(t, k); } catch (e) { toast('⚠️ לא הועבר: ' + e.message); } }
        closeModal();
      }}>העבר</button>
    </Sheet>
  );
}

function DupTx({ t }) {
  const from = t.month || keyOfISO(t.date);
  const [k, setK] = useState(from);
  async function go() {
    const body = { description: t.description, amount: t.amount, type: t.type, cat: t.cat, month: k, date: k === from ? t.date : shiftDateToPeriod(t.date, from, k), source: getTxSrc(t) };
    if (t.excluded === true) body.excluded = true;
    if (t.member_id) body.member_id = t.member_id;
    try { await insertTx(body); closeModal(); toast('📋 שוכפל'); } catch (e) { toast('שגיאה: ' + e.message); }
  }
  return (
    <Sheet title="📋 שכפול פעולה">
      <div className="sub mb12">"{t.description}" · <b className="red">{fmt(t.amount)}</b></div>
      <label className="lbl">שייך לתקופה</label>
      <PeriodSelect value={k} onChange={setK} extra={[from]} />
      <button className="ubtn ubtn-primary w100 mt12" onClick={go}>שכפל</button>
    </Sheet>
  );
}

function CopyPeriod({ fromKey }) {
  const [to, setTo] = useState(keyAdd(fromKey, 1));
  const [skip, setSkip] = useState(true);
  const n = S.tx.filter((t) => t.month === fromKey).length;
  if (!n) return <Sheet title="📋 העתקת תקופה"><div className="empty-s">אין פעולות בתקופה הנוכחית</div></Sheet>;
  return (
    <Sheet title="📋 העתקת תקופה">
      <div className="sub mb12">העתק את <b>{n} פעולות</b> מ-<b>{periodShort(fromKey)}</b> לתקופה:</div>
      <PeriodSelect value={to} onChange={setTo} extra={[]} />
      <label className="row mt10 sub" style={{ cursor: 'pointer' }}><input type="checkbox" checked={skip} onChange={(e) => setSkip(e.target.checked)} style={{ width: 'auto', minHeight: 0 }} /> דלג על כפילויות מדויקות</label>
      <button className="ubtn ubtn-primary w100 mt12" disabled={to === fromKey} onClick={() => { closeModal(); toast('מעתיק ' + n + ' פעולות...'); copyPeriod(fromKey, to, skip, (d) => shiftDateToPeriod(d, fromKey, to)); }}>העתק</button>
    </Sheet>
  );
}

async function deletePeriod(k) {
  const ids = S.tx.filter((t) => t.month === k).map((t) => t.id);
  if (!ids.length) return toast('אין פעולות בתקופה זו');
  if (!(await confirmDialog({ title: 'מחיקת תקופה', body: 'למחוק את כל ' + ids.length + ' הפעולות של ' + periodLabel(k) + '? פעולה זו לא ניתנת לביטול.', okLabel: 'מחק הכל', danger: true }))) return;
  try { await deleteTxs(ids); toast('✅ נמחקו ' + ids.length + ' פעולות'); } catch (e) { toast('⚠️ ' + e.message); }
}

function exportCSV(k) {
  // תא שמתחיל ב-= + - @ נפתח ב-Excel כנוסחה. גרש בהתחלה מונע את זה
  const qc = (s) => { s = String(s == null ? '' : s); if (/^[=+\-@]/.test(s)) s = "'" + s; return '"' + s.replace(/"/g, '""') + '"'; };
  let csv = '﻿תיאור,קטגוריה,סוג,סכום,תאריך,מקור,מוחרגת\n';
  S.tx.filter((t) => t.month === k).forEach((t) => {
    csv += [qc(t.description), qc(cat(t.type, t.cat).label), t.type === 'income' ? 'הכנסה' : 'הוצאה', t.amount, iso10(t.date), getTxSrc(t) === 'credit' ? 'אשראי' : 'עו"ש', isExcluded(t) ? 'כן' : ''].join(',') + '\n';
  });
  const a = document.createElement('a');
  a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
  a.download = 'תקציב_' + k + '.csv';
  a.click();
}

function Merchants({ txs, filtered }) {
  const [open, setOpen] = useState(null);
  const act = filterActive(txs);
  if (!act.length) return <Empty>{filtered ? 'אין פעולות עם הסינון הזה' : 'אין פעולות בתקופה זו'}</Empty>;
  const by = {};
  act.forEach((t) => {
    const n = (t.description || 'ללא שם').trim();
    const m = (by[n] = by[n] || { count: 0, total: 0, inc: 0, exp: 0, txs: [] });
    m.count++; m.total += Number(t.amount); m.txs.push(t);
    if (t.type === 'income') m.inc += Number(t.amount); else m.exp += Number(t.amount);
  });
  const sorted = Object.entries(by).sort((a, b) => b[1].total - a[1].total);
  // אחוז מסך ההוצאות. הכנסות (משכורת, החזר) לא נכנסות לחלוקה
  const total = sorted.reduce((s, e) => s + e[1].exp, 0);
  return (
    <>
      <div className="note mb8">{sorted.length} עסקים · סה"כ הוצאות {fmt(total)}</div>
      {sorted.map(([name, d]) => (
        <div key={name} className="clickable" onClick={() => setOpen(open === name ? null : name)} style={{ background: 'var(--surface)', borderRadius: 10, padding: '11px 13px', marginBottom: 7, border: '1px solid var(--border)' }}>
          <div className="row-b">
            <div className="row grow">
              <span style={{ fontSize: '1.1rem' }}>{d.exp > d.inc ? '💸' : d.inc > d.exp ? '💰' : '🔄'}</span>
              <div className="grow"><div className="ellipsis" style={{ fontWeight: 700, fontSize: '.93rem' }}>{name}</div>
                <div className="note">{d.count === 1 ? 'פעולה אחת' : d.count + ' פעולות'}{d.exp && total ? ' · ' + Math.round((d.exp / total) * 100) + '% מההוצאות' : ''}{d.count > 1 ? ' · ממוצע ' + fmt(d.total / d.count) : ''}</div></div>
            </div>
            <div className="nowrap" style={{ fontWeight: 800, fontSize: '.95rem', color: d.exp > d.inc ? 'var(--red)' : 'var(--green)' }}>{fmt(d.total)}</div>
          </div>
          {open === name && (
            <div className="mt10" style={{ paddingTop: 10, borderTop: '1px solid var(--border)' }}>
              {d.txs.map((t) => (
                <div key={t.id} className="row-b" style={{ fontSize: '.86rem', padding: '4px 0' }}>
                  <span>{cat(t.type, t.cat).icon} {iso10(t.date)}</span>
                  <span style={{ color: t.type === 'income' ? 'var(--green)' : 'var(--red)' }}>{fmt(t.amount, t.type === 'income' ? '+' : '-')}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </>
  );
}
